import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import AppBar from "../../common/AppBar";
import Loader from "../../common/Loader";
import Snackbar from "../../common/Snackbar";
import { SaveModal } from "../../common/Modals";
import CategoryPopup from "../Category/CategoryPopup";
import VersionHistoryPopup from "../../common/VersionHistoryPopup/VersionHistoryPopup";
import { useVersionHistoryDataFromNodes } from "../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes.js";
import { getAllCategories } from "../../redux/categorySlice";
import {
  createStep,
  updateStep,
  getAllStepsForSection,
} from "../../redux/stepSlice";
import { getAllSectionsForPart } from "../../redux/sectionSlice";
import { getAllVersionHistories } from "../../redux/versionHistorySlice";
import { getAllParts, getPartById } from "../../redux/partSlice";
import { getAllUnits } from "../../redux/unitSlice";
import { getAllSuggestionRules } from "../../redux/suggestionSlice";
import { generateSHA256 } from "../../util/generateSHA256";
import { getUnitNameById } from "../../util/AppUtils";
import { generateStepPreview } from "../../util/stepUtils";
import { getAuditTrailData } from "../../util/getAuditTrailData";
import { openPreview } from "../../redux/previewSlice";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChangesBlocker";
import { buildPermissionContext } from "../../util/permissions";
import { cleanBaselineHtml } from "../../util/lexical";
import { setTaskReference } from "../Tasks/Uitlities";
import useStepData from "./hooks/useStepData";
import useStepForm from "./hooks/useStepForm";
import useStepPersistence from "./hooks/useStepPersistence";
import StepForm from "./components/StepForm";
import StepDetailsTable from "./components/StepDetailsTable";
import BOMTable from "./components/BOMTable";
import "./AddEditStep.css";
import { bomRowsMapper, StepDetailsMapper } from "../Documents/Utils/utils.js";

const AddEditStep = () => {
  const {
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref: step_id_param,
  } = useParams();
  const [searchParams] = useSearchParams();
  const step_id_query = searchParams.get("step_id_ref");

  // step_id_ref can come from URL params (edit mode) or query params (add mode after specific step)
  const step_id_ref = step_id_param || step_id_query;
  const location = useLocation();
  // Determine mode based on URL path
  const mode = location.pathname.includes("/edit-step") ? "edit" : "add";
  const {
    loading,
    stepTitle,
    subheading,
    sectionTitle,
    sectionIdRef,
    siblingOrder,
    currentStepData,
    allNodesData,
    currentStatus,
    item_code_type
  } = useStepData({
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    partnumber,
    revision,
    mode,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;
  // Snackbar handlers
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };
  // Unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Tracks whether the Lexical short-description editor has undo history,
  // meaning the user has genuinely typed in it since it was initialised.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);
  // Ensure defaults for categories are set only once in add mode
  const [defaultsInitialized, setDefaultsInitialized] = useState(false);

  // Use custom hooks for form state management
  const {
    shortDescription,
    setShortDescription,
    stepDetails,
    setStepDetails,
    bomTable,
    setBomTable,
    bomTableCategory,
    setBomTableCategory,
    bomTableCategoryId,
    setBomTableCategoryId,
    partNumberCategory,
    setPartNumberCategory,
    partTableCategoryId,
    setPartTableCategoryId,
    isHideStepHeading,
    setIsHideStepHeading,
    isHideStepDetailsHeading,
    setIsHideStepDetailsHeading,
    isHideBomHeading,
    setIsHideBomHeading,
    editingStepIndex,
    setEditingStepIndex,
    editingBomIndex,
    setEditingBomIndex,
    isModalOpen,
    setModalOpen,
    comment,
    setComment,
    isPartsModalOpen,
    setPartsModalOpen,
    selectedPartIndex,
    setSelectedPartIndex,
    selectedTable,
    setSelectedTable,
    categoryModalOpenForBom,
    setCategoryModalOpenForBom,
    categoryModalOpenForPart,
    setCategoryModalOpenForPart,
    handleShortDescriptionChange,
    handleHideStepHeadingChange,
    handleHideStepDetailsHeadingChange,
    handleHideBomHeadingChange,
    handleBomTableCategoryChange,
    handleBomTableCategoryClear,
    handlePartNumberCategoryChange,
    handlePartNumberCategoryClear,
    handleStepDetailsChange,
    handleBomTableChange,
    addStepRow,
    addBomRow,
    handleSelectPart,
    resetForm,
  } = useStepForm({
    mode,
    initialData: {},
    onUnsavedChanges: () => setHasUnsavedChanges(true),
    snackbar: showSnackbar,
  });

  // Use persistence hook
  const {
    localStorageRestored,
    hasLocalStorageData,
    saveForPreview,
    clearStoredData,
  } = useStepPersistence({
    mode,
    stepId: step_id_ref,
    shortDescription,
    stepDetails,
    bomTable,
    bomTableCategory,
    bomTableCategoryId,
    partNumberCategory,
    partTableCategoryId,
    isHideStepHeading,
    isHideStepDetailsHeading,
    isHideBomHeading,
    setShortDescription,
    setStepDetails,
    setBomTable,
    setBomTableCategory,
    setBomTableCategoryId,
    setPartNumberCategory,
    setPartTableCategoryId,
    setIsHideStepHeading,
    setIsHideStepDetailsHeading,
    setIsHideBomHeading,
  });

  // Basic state (not in hooks)
  const [isLoading, setIsLoading] = useState(false);
  const [lastSavedText, setLastSavedText] = useState("");

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  // Version History state
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Redux selectors
  const { units } = useSelector((state) => state.units);
  const { categories } = useSelector((state) => state.category);
  const { versionHistories, loading: versionHistoryLoading } = useSelector(
    (state) => state.versionHistory,
  );

  const versionHistoryBreadcrumbFallback = useMemo(() => {
    const bits = [partnumber, revision, sectionTitle, stepTitle].filter(
      (x) => x != null && String(x).trim() !== "",
    );
    if (bits.length < 2) return "";
    const [pn, rev, ...rest] = bits;
    return [`${pn}`, `Rev ${rev}`, ...rest].join(" > ");
  }, [partnumber, revision, sectionTitle, stepTitle]);

  const sectionDataForVH = useMemo(
    () => allNodesData?.find((s) => s.tree_node_id === section_id_ref),
    [allNodesData, section_id_ref],
  );

  const stepDataForVH = useMemo(
    () =>
      sectionDataForVH?.steps?.find((st) => st.tree_node_id === step_id_ref),
    [sectionDataForVH, step_id_ref],
  );

  const stepVersionHistoryMatchIds = useMemo(
    () =>
      [
        step_id_ref,
        stepDataForVH?.step_id_ref,
        stepDataForVH?.detail?.step_tree_node_id,
        currentStepData?.step_id_ref,
        currentStepData?.detail?.step_tree_node_id,
      ].filter(Boolean),
    [
      step_id_ref,
      stepDataForVH?.step_id_ref,
      stepDataForVH?.detail?.step_tree_node_id,
      currentStepData?.step_id_ref,
      currentStepData?.detail?.step_tree_node_id,
    ],
  );

  /** Procedure-wide step index — same logic as ViewStep Node History. */
  const globalStepOrdinalForNodeHistory = useMemo(() => {
    if (!section_id_ref || !step_id_ref || !Array.isArray(allNodesData)) {
      return null;
    }
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      return intA - intB;
    };

    const sortedSections = [...allNodesData].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    const sectionIndex = sortedSections.findIndex(
      (section) => section.tree_node_id === section_id_ref,
    );
    if (sectionIndex < 0) return null;

    const sec = sortedSections[sectionIndex];
    const steps = sec?.steps || [];
    const stepIdx = steps.findIndex((st) => st.tree_node_id === step_id_ref);
    if (stepIdx < 0) return null;

    const globalStepStart = sortedSections
      .slice(0, sectionIndex)
      .reduce((total, section) => total + (section.steps?.length || 0), 0);

    return globalStepStart + stepIdx + 1;
  }, [allNodesData, section_id_ref, step_id_ref]);

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory && mode === "edit",
    revisionId,
    partId,
    allRevisionNodes: allNodesData,
    fallbackVersionId:
      currentStepData?.detail?.version_id_ref ||
      currentStepData?.version_id_ref ||
      null,
    nodesReady:
      mode === "edit" &&
      !loading &&
      Array.isArray(allNodesData) &&
      allNodesData.length > 0,
  });

  const { suggestions } = useSelector((state) => state.suggestion);

  // Stable callback forwarded to ShortDescriptionEditor via StepForm.
  // Fires with `true` when Lexical's undo stack becomes non-empty (real user
  // edit) and `false` when it empties again (user undid everything).
  const handleShortDescriptionDirtyChange = useCallback(
    (dirty) => setIsShortDescriptionDirty(dirty),
    []
  );

  // Navigation protection – block if either field group is dirty
  useUnsavedChangesBlocker(hasUnsavedChanges || isShortDescriptionDirty);

  // AppBar back handler
  const handleAppBarBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch categories, parts, units, and suggestion rules on mount
  useEffect(() => {
    dispatch(getAllCategories({ limit: 100, offset: 0 }));
    dispatch(getAllParts({ limit: 100, offset: 0 }));
    dispatch(getAllUnits({ limit: 1000, offset: 0 }));
    dispatch(getAllSuggestionRules({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  // Set default category values for add mode (only once)
  useEffect(() => {
    if (mode !== "add") return;
    if (defaultsInitialized) return;
    if (categories.length === 0) return;

    let didSetAny = false;
    if (!bomTableCategory) {
      for (let category of categories) {
        if (category.category_name === "Items Required") {
          setBomTableCategoryId(category.category_id);
          setBomTableCategory(category.category_name);
          didSetAny = true;
          break;
        }
      }
    }

    if (!partNumberCategory) {
      for (let category of categories) {
        if (category.category_name === "Item Code") {
          setPartTableCategoryId(category.category_id);
          setPartNumberCategory(category.category_name);
          didSetAny = true;
          break;
        }
      }
    }

    // Mark as initialized so user clear action won't be overridden later
    setDefaultsInitialized(true);
  }, [mode, categories, defaultsInitialized]);

  // Populate form data in edit mode
  useEffect(() => {
    if (mode === "edit" && currentStepData) {
      const { detail } = currentStepData;

      // Process short description to handle both plain text and HTML content
      let processedShortDescription = detail.short_description || "";
      if (processedShortDescription && !processedShortDescription.includes('<div class="editor-shell">')) {
        // If it's plain text (no editor-shell wrapper), convert it to HTML format
        processedShortDescription = `<p>${processedShortDescription}</p>`;
      }

      // Set form fields
      setShortDescription(cleanBaselineHtml(processedShortDescription));
      setIsHideStepHeading(detail.is_hide_step_heading || false);
      setIsHideStepDetailsHeading(detail.is_hide_step_details || false);
      setIsHideBomHeading(detail.is_hide_BOM_table || false);

      // Set category fields
      const bomTitleInfo =
        detail.step_data?.bom_table_title_info || detail.bom_table_title_info;
      const itemCodeTitleInfo =
        detail.step_data?.part_number_column_title_ref_info ||
        detail.part_number_column_title_ref_info;

      setBomTableCategory(bomTitleInfo?.category_name || "");
      setBomTableCategoryId(bomTitleInfo?.category_id || "");
      setPartNumberCategory(itemCodeTitleInfo?.category_name || "");
      setPartTableCategoryId(itemCodeTitleInfo?.category_id || "");

      // Process step details
      if (
        detail.step_data?.step_details &&
        Array.isArray(detail.step_data?.step_details)
      ) {
        const enrichedSteps = detail.step_data.step_details.map((step) => ({
          ...step,
          partNumber: step.part_info?.part_number || "",
          partId: step.part_info?.part_id,
          description: step.part_info?.description || "",
          unit_ref: step.part_info?.unit_type_info?.unit_type_id,
          unitId: step.part_info?.unit_type_info?.unit_type_id,
        }));

        // Check if orders field exists in the data (even if 0)
        // This ensures sequence is preserved regardless of Qty being entered or not
        const hasOrdersField = enrichedSteps.some((step) => step.hasOwnProperty('orders'));

        let sortedSteps;
        if (hasOrdersField) {
          // Sort by orders field to preserve the saved sequence
          // This preserves the exact order as entered in Step Details Table, regardless of Qty
          // Fallback to created_at if orders are equal
          sortedSteps = enrichedSteps.sort((a, b) => {
            const orderA = parseInt(a.orders ?? 0, 10);
            const orderB = parseInt(b.orders ?? 0, 10);
            if (orderA !== orderB) return orderA - orderB;
            // Fallback to created_at in ascending order if orders are equal
            return new Date(a.created_at || 0) - new Date(b.created_at || 0);
          });
        } else {
          // If orders field is completely missing, sort by created_at in ascending order
          sortedSteps = enrichedSteps.sort((a, b) => {
            return new Date(a.created_at || 0) - new Date(b.created_at || 0);
          });
        }

        setStepDetails(sortedSteps);
      }

      // Process BOM table (parts_required)
      if (
        detail.step_data?.parts_required &&
        Array.isArray(detail.step_data?.parts_required)
      ) {
        // First, create initial enriched rows
        const enrichedBomRows = detail.step_data.parts_required.map((item) => {
          // Try multiple paths to get unit_type_id_ref
          // Priority: part_info.unit_type_id_ref (direct) > part_info.unit_info.unit_type_id_ref > others
          const unitTypeIdRef =
            item.part_info?.unit_type_id_ref ||
            item.part_info?.unit_info?.unit_type_id_ref ||
            item.unit_type_id_ref ||
            item.unit_ref;

          // Get unit_id from unit_info if available, otherwise from part_info
          const unitId = item.part_info?.unit_info?.unit_id || item.unit || "";

          return {
            ...item,
            partNumber: item.part_info?.part_number || "",
            type: item.part_info?.type_info?.name || "",
            type_details: item.part_info?.type_info, // Preserve type_details with orders field
            description: item.part_info?.description || "",
            table_note: item.part_info?.table_note,
            qty: item.qty || "",
            unit: unitId,
            partId: item.part_info?.part_id,
            unit_type_id_ref: unitTypeIdRef,
            unit_ref: unitTypeIdRef,
            parts_required_id: item.parts_required_id,
          };
        });

        // Fetch part details for all parts to get accurate unit_type_id_ref
        const fetchPartDetails = async () => {
          try {
            // Helper function to check if a part matches a suggestion rule
            const checkPartMatchesRule = (part, rule) => {
              if (!part || !rule) return false;

              // Check if part type matches rule's applicable types
              const partTypeId = part.type_info?.type_id || part.type_details?.type_id || part.type_id;
              const ruleTypeIds =
                rule.type_ids || rule.mapped_types?.map((t) => t.type_id) || [];

              if (ruleTypeIds.length > 0 && !ruleTypeIds.includes(partTypeId)) {
                return false;
              }

              // Check if part number matches rule criteria
              const partNumber = part.part_number || "";
              const criteria = rule.criteria || "";
              const criteriaFormat =
                rule.criteria_format_name || rule.criteria_format_ref || "";
              const criteriaFormatLower = criteriaFormat?.toLowerCase() || "";

              if (!criteria) return false;

              try {
                // Check for Regular Expression format
                if (
                  criteriaFormat === "Regular Expression" ||
                  criteriaFormatLower.includes("regular") ||
                  criteriaFormatLower.includes("regex") ||
                  criteriaFormatLower.includes("expression")
                ) {
                  const regex = new RegExp(criteria);
                  return regex.test(partNumber);
                }
                // Check for Glob format
                else if (
                  criteriaFormat === "Glob" ||
                  criteriaFormatLower.includes("glob")
                ) {
                  const globToRegex = (pattern) => {
                    return pattern
                      .replace(/\./g, "\\.")
                      .replace(/\*/g, ".*")
                      .replace(/\?/g, ".");
                  };
                  const regex = new RegExp(`^${globToRegex(criteria)}$`);
                  return regex.test(partNumber);
                }
                // Default to Text format (exact equality only)
                else {
                  return partNumber === criteria;
                }
              } catch (error) {
                console.error("Error matching rule criteria:", error);
                return false;
              }
            };

            // Get matching suggestions for a part
            const getMatchingSuggestions = (part) => {
              if (!part || !suggestions || suggestions.length === 0) return [];

              // Get all matching rules
              const matchingRules = suggestions.filter((rule) =>
                checkPartMatchesRule(part, rule)
              );

              if (matchingRules.length === 0) return [];

              // Extract all suggestion names
              const allSuggestions = matchingRules
                .map((rule) => rule.rule_name || rule.suggestion || "")
                .filter(Boolean);

              // Deduplicate by name - keep only unique suggestion names while preserving order
              const uniqueSuggestions = allSuggestions.filter(
                (value, index, self) => self.indexOf(value) === index
              );

              return uniqueSuggestions;
            };

            // Get all unique part IDs from BOM rows
            const partIdsToFetch = enrichedBomRows
              .filter((row) => row.partId)
              .map((row) => row.partId)
              .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

            // If no parts to fetch, still calculate suggestions if available
            if (partIdsToFetch.length === 0) {
              // Calculate suggestions for rows that have partId but no suggestions
              const rowsWithSuggestions = enrichedBomRows.map((row) => {
                if (row.partId && (!row.suggestions || row.suggestions.length === 0) && suggestions && suggestions.length > 0) {
                  const partForSuggestion = {
                    part_id: row.partId,
                    part_number: row.partNumber,
                    type_info: row.type_details,
                    type_details: row.type_details,
                    type_id: row.type_details?.type_id,
                    description: row.description,
                    table_note: row.table_note,
                  };
                  const matchingSuggestions = getMatchingSuggestions(partForSuggestion);
                  if (matchingSuggestions.length > 0) {
                    return { ...row, suggestions: matchingSuggestions };
                  }
                }
                return row;
              });

              // Sort by Type orders (ascending) then by Item Code
              const sortedBomRows = rowsWithSuggestions.sort((a, b) => {
                const orderA = parseInt(a.type_details?.orders || 999999, 10);
                const orderB = parseInt(b.type_details?.orders || 999999, 10);
                if (orderA !== orderB) return orderA - orderB;

                const partNumberA = a.partNumber || "";
                const partNumberB = b.partNumber || "";
                const splitPart = (str) => {
                  const match = str.match(/^(\d+)?([A-Za-z].*)?$/);
                  if (!match) return { num: NaN, rest: str };
                  return {
                    num: match[1] ? parseInt(match[1], 10) : NaN,
                    rest: match[2] || str,
                  };
                };
                const aSplit = splitPart(partNumberA);
                const bSplit = splitPart(partNumberB);
                if (!isNaN(aSplit.num) && isNaN(bSplit.num)) return -1;
                if (isNaN(aSplit.num) && !isNaN(bSplit.num)) return 1;
                if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
                  if (aSplit.num !== bSplit.num) return aSplit.num - bSplit.num;
                  return aSplit.rest.localeCompare(bSplit.rest);
                }
                return partNumberA.localeCompare(partNumberB);
              });
              setBomTable(sortedBomRows);
              return;
            }


            // Fetch all parts in parallel
            const partDetailsPromises = partIdsToFetch.map((partId) =>
              dispatch(getPartById(partId))
                .unwrap()
                .catch((error) => {
                  console.error(`❌ Error fetching part ${partId}:`, error);
                  return null; // Return null for failed fetches
                })
            );

            const partDetailsResults = await Promise.all(partDetailsPromises);

            // Create a map of partId -> partData for suggestions
            const partDataMap = {};
            // Create a map of partId -> unit_type_id_ref
            const partUnitTypeMap = {};
            partDetailsResults.forEach((partData, index) => {
              if (!partData) return; // Skip failed fetches

              const partId = partIdsToFetch[index];

              // Store full part data for suggestions
              partDataMap[partId] = partData;

              // Log the full response structure for debugging

              // Try multiple paths to get unit_type_id_ref from the fetched part
              // The API might return data directly or wrapped in a results object
              const unitTypeIdRef =
                partData?.unit_type_id_ref ||
                partData?.unit_info?.unit_type_id_ref ||
                partData?.results?.unit_type_id_ref ||
                partData?.results?.unit_info?.unit_type_id_ref ||
                (partData?.results &&
                  typeof partData.results === "object" &&
                  partData.results.unit_type_id_ref) ||
                (partData?.results &&
                  typeof partData.results === "object" &&
                  partData.results.unit_info?.unit_type_id_ref);

              if (unitTypeIdRef) {
                partUnitTypeMap[partId] = unitTypeIdRef;
              } else {
              }
            });

            // Update enriched rows with fetched unit_type_id_ref and suggestions
            // ALWAYS use fetched data to ensure accuracy
            const updatedBomRows = enrichedBomRows.map((row) => {
              let updatedRow = { ...row };

              // Update unit_type_id_ref if available
              if (row.partId && partUnitTypeMap[row.partId]) {
                updatedRow.unit_type_id_ref = partUnitTypeMap[row.partId];
                updatedRow.unit_ref = partUnitTypeMap[row.partId];
              } else if (row.unit_type_id_ref) {
              } else {
              }

              // Fetch suggestions if part data is available
              if (row.partId && partDataMap[row.partId] && suggestions && suggestions.length > 0) {
                const partData = partDataMap[row.partId];
                // Construct part object for suggestion matching
                const partForSuggestion = {
                  part_id: partData.part_id || row.partId,
                  part_number: partData.part_number || row.partNumber,
                  type_info: partData.type_info || row.type_details,
                  type_details: partData.type_info || row.type_details,
                  type_id: partData.type_info?.type_id || row.type_details?.type_id,
                  description: partData.description || row.description,
                  table_note: partData.table_note,
                };

                const matchingSuggestions = getMatchingSuggestions(partForSuggestion);
                if (matchingSuggestions.length > 0) {
                  updatedRow.suggestions = matchingSuggestions;
                }
              }

              return updatedRow;
            });

            // Sort by Type orders (ascending) then by Item Code
            const sortedBomRows = updatedBomRows.sort((a, b) => {
              const orderA = parseInt(a.type_details?.orders || 999999, 10);
              const orderB = parseInt(b.type_details?.orders || 999999, 10);
              if (orderA !== orderB) return orderA - orderB;

              const partNumberA = a.partNumber || "";
              const partNumberB = b.partNumber || "";
              const splitPart = (str) => {
                const match = str.match(/^(\d+)?([A-Za-z].*)?$/);
                if (!match) return { num: NaN, rest: str };
                return {
                  num: match[1] ? parseInt(match[1], 10) : NaN,
                  rest: match[2] || str,
                };
              };
              const aSplit = splitPart(partNumberA);
              const bSplit = splitPart(partNumberB);
              if (!isNaN(aSplit.num) && isNaN(bSplit.num)) return -1;
              if (isNaN(aSplit.num) && !isNaN(bSplit.num)) return 1;
              if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
                if (aSplit.num !== bSplit.num) return aSplit.num - bSplit.num;
                return aSplit.rest.localeCompare(bSplit.rest);
              }
              return partNumberA.localeCompare(partNumberB);
            });

            // Log final BOM table data

            setBomTable(sortedBomRows);
          } catch (error) {
            console.error("Error fetching part details for BOM table:", error);
            // Fallback: set table with initial data even if fetch fails
            setBomTable(enrichedBomRows);
          }
        };

        // Fetch part details and update BOM table
        fetchPartDetails();
      }
    }
  }, [mode, currentStepData]);

  // Fetch version history in edit mode to get the actual modifier user
  useEffect(() => {
    if (mode === "edit" && currentStepData) {
      const versionIdRef =
        currentStepData?.version_id_ref ||
        currentStepData?.detail?.version_id_ref;
      if (versionIdRef) {
        dispatch(getAllVersionHistories({ versionId: versionIdRef }));
      }
    }
  }, [mode, currentStepData, dispatch]);

  // Update last saved text when version history is loaded
  useEffect(() => {
    if (mode !== "edit" || !currentStepData || versionHistories.length === 0)
      return;

    // Get the latest version history entry (most recent audit)
    const latestVersion = versionHistories[0];
    if (latestVersion) {
      const userName =
        `${latestVersion.first_name || ""} ${latestVersion.last_name || ""
          }`.trim() || "Unknown User";

      const timestamp = latestVersion.audit_date || currentStepData?.updated_at;
      if (timestamp) {
        const { generateLastSavedText } = require("../../util/stepUtils");
        setLastSavedText(generateLastSavedText(timestamp, userName));
      }
    }
  }, [mode, currentStepData, versionHistories]);

  // Preview handler - Constructs draft step for preview
  const handlePreviewClick = async () => {
    // Validate mandatory fields before showing preview
    if (bomTableCategory.length === 0) {
      showSnackbar("Please fill the mandatory fields", "error");
      return;
    }

    if (partTableCategoryId.length === 0) {
      showSnackbar("Please fill the mandatory fields", "error");
      return;
    }

    // For edit mode, save draft to localStorage for persistence
    if (mode === "edit") {
      try {
        await saveForPreview();
      } catch (err) {
        // Continue with preview even if save fails
      }
    }

    // Construct draft step from current form data
    const draftStep = {
      tree_node_id: mode === "edit" ? step_id_ref : `draft-step-${Date.now()}`,
      mode: mode, // "add" or "edit"
      isDraft: true,
      sibling_order: siblingOrder,
      section_id_ref: section_id_ref, // Section this step belongs to
      detail: {
        short_description: shortDescription,
        is_hide_step_heading: isHideStepHeading,
        is_hide_step_details: isHideStepDetailsHeading,
        is_hide_BOM_table: isHideBomHeading,
        bom_table_title_info: {
          category_name: bomTableCategory,
          category_id: bomTableCategoryId,
        },
        part_number_column_title_ref_info: {
          category_name: partNumberCategory,
          category_id: partTableCategoryId,
        },
        step_data: {
          step_details: stepDetails.map((step, index) => ({
            ...step,
            action: step.action,
            qty: step.qty,
            orders: index + 1, // Preserve order for preview
            part_info: {
              part_number: step.partNumber,
              part_id: step.partId,
              description: step.description,
              unit_type_info: {
                unit_type_id: step.unitId,
                unit_name: getUnitNameById(step.unitId, units),
              },
            },
          })),
          bom_table_title_info: {
            category_name: bomTableCategory,
            category_id: bomTableCategoryId,
          },
          part_number_column_title_ref_info: {
            category_name: partNumberCategory,
            category_id: partTableCategoryId,
          },
          parts_required: bomTable.map((item) => ({
            ...item,
            part_info: {
              part_number: item.partNumber,
              part_id: item.partId,
              description: item.description,
              table_note: item.table_note,
              type_info: item.type_details || {
                name: item.type,
                orders: 999999,
              }, // Include orders for sorting
              unit_info: {
                unit_id: item.unit,
                unit_type_id_ref: item.unit_ref,
                unit_name: getUnitNameById(item.unit, units), // Add unit_name for display
              },
            },
            qty: item.qty,
          })),
        },
      },
      substeps:
        mode === "edit" && currentStepData?.substeps
          ? currentStepData.substeps
          : [],
      children:
        mode === "edit" && currentStepData?.children
          ? currentStepData.children
          : [],
    };

    // Prepare BOM items for merging into main BOM table
    const draftBomItems = bomTable.map((item) => ({
      partNumber: item.partNumber,
      partId: item.partId,
      description: item.description,
      type: item.type,
      type_details: item.type_details || { orders: 999999 }, // Preserve type_details for sorting
      qty: item.qty || 0,
      unit: item.unit,
      unit_name: getUnitNameById(item.unit, units),
    }));

    dispatch(
      openPreview({
        partId,
        revisionId,
        draftStep,
        draftBomItems,
      })
    );
  };

  // Save handler - Validation
  const handleSave = () => {
    // Check if any row is currently being edited
    if (editingStepIndex !== null) {
      showSnackbar(
        "Please save or cancel the Step Details row you are currently editing before saving.",
        "error"
      );
      return;
    }

    if (editingBomIndex !== null) {
      showSnackbar(
        "Please save or cancel the BOM table row you are currently editing before saving.",
        "error"
      );
      return;
    }

    if (bomTableCategory.length === 0) {
      showSnackbar("Please select BOM Table for Step Title.", "error");
      return;
    }

    if (partTableCategoryId.length === 0) {
      showSnackbar("Please select Item Code Column Title.", "error");
      return;
    }

    // Validate Step Details rows - check for blank or incomplete rows
    const hasIncompleteStepRows = stepDetails.some((row) => {
      // If the row is completely blank, that's okay (will be filtered out)
      const isCompletelyBlank = !row.action && !row.partNumber && !row.qty;
      if (isCompletelyBlank) return false;

      // If row has any data, action must be filled
      if (!row.action || row.action.trim() === "") {
        return true;
      }

      return false;
    });

    if (hasIncompleteStepRows) {
      showSnackbar(
        "Please complete all Step Details rows or remove incomplete rows before saving.",
        "error"
      );
      return;
    }

    // Validate BOM Table rows - check for blank or incomplete rows
    const hasIncompleteBomRows = bomTable.some((row) => {
      // If the row is completely blank, that's okay (will be filtered out)
      const isCompletelyBlank =
        !row.partNumber && !row.type && !row.qty && !row.unit;
      if (isCompletelyBlank) return false;

      // If row has any data, partNumber must be filled
      if (!row.partNumber || row.partNumber.trim() === "") {
        return true;
      }

      // If unit is selected, qty must be filled
      if (row.unit && (!row.qty || row.qty === 0)) {
        return true;
      }

      return false;
    });

    if (hasIncompleteBomRows) {
      showSnackbar(
        "Please complete all BOM table rows or remove incomplete rows before saving.",
        "error"
      );
      return;
    }

    setModalOpen(true);
  };

  // Confirm save handler - Main save logic
  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    setIsLoading(true);

    // Filter out completely blank rows from Step Details before saving
    const validStepRows = stepDetails.filter((item) => {
      return item.action && item.action.trim() !== "";
    });

    // Filter out completely blank rows from BOM table before saving
    const validBomRows = bomTable.filter((item) => {
      return item.partNumber && item.partNumber.trim() !== "";
    });

    const stepData = {
      short_description: shortDescription,
      revision_id_ref: revisionId,
      section_id_ref: sectionIdRef,
      part_required_id_ref: partId,
      is_hide_step_heading: isHideStepHeading,
      is_hide_step_details: isHideStepDetailsHeading,
      is_hide_BOM_table: isHideBomHeading,
      BOM_table_for_step_title_ref: bomTableCategoryId,
      part_number_column_title_ref: partTableCategoryId,
      part_details: validBomRows.map((item) => ({
        qty: item.qty?.length === 0 ? 0 : item.qty,
        part_ref: item.partId,
        unit_ref: item.unit?.length === 0 ? null : item.unit,
        unit_name: getUnitNameById(item.unit, units),
        type: item.type,
        description: item.description,
        table_note: item.table_note,
        partNumber: item.partNumber,
      })),
      section_details: validStepRows.map((item, index) => ({
        action: item.action,
        qty: item.qty?.length === 0 ? 0 : item.qty,
        part_ref: item.partId && item.partId.trim() !== "" ? item.partId : null,
        orders: index + 1, // Add order field to preserve user's intended order
      })),
      // sibling_order set per mode below to avoid accidental changes on edit
    };

    const breadcrumb_text = `${partnumber} > Rev ${revision} > ${sectionTitle} > ${stepTitle}`;
    const pageTitle = stepTitle;

    // Determine the correct sibling order to send based on mode
    const effectiveSiblingOrder =
      mode === "add"
        ? siblingOrder
        : currentStepData?.sibling_order ||
        currentStepData?.procedure_tree?.sibling_order ||
        currentStepData?.procedure_tree?.sibling_order;

    const part_details_preview = validBomRows.map((item) => ({
      qty: item.qty?.length === 0 ? 0 : item.qty,
      part_ref: item.partId,
      unit_ref: item.unit?.length === 0 ? null : item.unit,
      unit_name: getUnitNameById(item.unit, units),
      type: item.type,
      description: item.description,
      table_note: item.table_note,
      partNumber: item.partNumber,
      part_info: {
        type_info: {
          orders: item.type_details?.orders || item.part_info?.type_info?.orders || 999999
        }
      },
    }));


    const step_data_prepared = validStepRows.map((item, index) => ({
      action: item.action,
      qty: item.qty?.length === 0 ? 0 : item.qty,
      partNumber: item.partNumber,
      description: item.description,
      orders: item.orders || index + 1,
      part_ref: item.partId,
    }));

    stepData.step_data_prepared = {
      bom_table_title_info: {
        category_name: bomTableCategory,
        category_id: bomTableCategoryId,
      },
      part_number_column_title_ref_info: {
        category_name: partNumberCategory,
        category_id: partTableCategoryId,
      },
      parts_required: bomRowsMapper(part_details_preview),
      step_details: StepDetailsMapper(step_data_prepared),
    }

    try {
      if (mode === "add") {
        // attach sibling_order only for create
        stepData.sibling_order = effectiveSiblingOrder;
        const data = await dispatch(
          createStep({
            stepData,
            comment,
            version_content: {
              action_to_show: `Save ${stepTitle} for ${partnumber}, Rev ${revision}`,
              partNumber: partnumber,
              revision,
              status:currentStatus,
              item_code_type,
              type:"step"
            },
            allNodesData,
            partnumber,
            revision,
          })
        ).unwrap();

        if (data.success) {
          showSnackbar("Step created successfully!", "success");
          setHasUnsavedChanges(false);
          clearStoredData();
          setIsLoading(false);
          setModalOpen(false);
          setTimeout(() => {
            navigate(
              `/view-step/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${data.procedureTree.tree_node_id}`
            );
          }, 1000);
        }
      } else {
        // Edit mode
        stepData.procedure_tree_id = step_id_ref;
        stepData.step_tree_node_id = currentStepData.step_id_ref;

        const audit_trail_data = await getAuditTrailData({
          action: "UPDATE_STEP",
          comment: comment,
          parent_version: currentStepData?.version_id_ref,
          version_content: {
            action_to_show: `Edit ${stepTitle} for ${partnumber}, Rev ${revision}`,
            partNumber: partnumber,
            revision,
            status:currentStatus,
            item_code_type,
            type: "step",
          },
        });
        stepData.audit_trail_data = audit_trail_data;
        // Attach sibling_order to update payload to match existing order exactly
        stepData.sibling_order = effectiveSiblingOrder;

        stepData.step_data_prepared = {
          bom_table_title_info: {
            category_name: bomTableCategory,
            category_id: bomTableCategoryId,
          },
          part_number_column_title_ref_info: {
            category_name: partNumberCategory,
            category_id: partTableCategoryId,
          },
          parts_required: bomRowsMapper(part_details_preview),
          step_details: validStepRows,
        }

        const data = await dispatch(
          updateStep({
            data: stepData,
            id: step_id_ref,
            allNodesData,
            partnumber,
            revision,
            tree_node_id:currentStepData.tree_node_id
          })
        ).unwrap();
        showSnackbar("Step updated successfully!", "success");
        setHasUnsavedChanges(false);
        clearStoredData();
        setModalOpen(false);
        setIsLoading(false);

        setTimeout(() => {
          navigate(
            `/view-step/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`
          );
        }, 1000);
      }
    } catch (error) {
      setIsLoading(false);
      showSnackbar(
        `Error ${mode === "add" ? "creating" : "updating"} step: ` +
        error?.message,
        "error"
      );
    }
  };

  // Set task reference for edit mode
  useEffect(() => {
    if (mode === "edit" && currentStepData) {
      setTaskReference(
        dispatch,
        `Step ${partnumber}`,
        currentStepData?.detail?.step_tree_node_id,
        currentStepData?.tree_node_id,
        currentStepData?.ltree_path,
      );
    }
  }, [mode, currentStepData, dispatch, partnumber]);

  if (loading) {
    return <Loader text="Loading step data..." />;
  }

  return (
    <div>
      <AppBar
        onBack={handleAppBarBack}
        subTitleAboveTitle={
          <>
            {localStorage.getItem("user_name")} is{" "}
            {mode === "add" ? "Creating" : "Editing"}
          </>
        }
        title={stepTitle}
        showTablesDropdown={true}
        buttons={[
          {
            label: "Preview",
            onClick: handlePreviewClick,
          },
          {
            label: "Save",
            onClick: canEdit ? handleSave : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save steps."
              : undefined,
          },
        ]}
        subheading={subheading}
        showBlackTitle={true}
        showColorTitle={false}
        subheadingAfterTitle={mode === "edit" ? lastSavedText : null}
        versionHistoryHandler={
          mode === "edit" ? () => setShowVersionHistory(true) : null
        }
      />

      <StepForm
        mode={mode}
        versionId={currentStepData?.version_id_ref || ""}
        isHideStepHeading={isHideStepHeading}
        onHideStepHeadingChange={handleHideStepHeadingChange}
        bomTableCategory={bomTableCategory}
        onBomCategoryPencilClick={() => setCategoryModalOpenForBom(true)}
        onBomCategoryClear={handleBomTableCategoryClear}
        partNumberCategory={partNumberCategory}
        onPartCategoryPencilClick={() => setCategoryModalOpenForPart(true)}
        onPartCategoryClear={handlePartNumberCategoryClear}
        shortDescription={shortDescription}
        onShortDescriptionChange={handleShortDescriptionChange}
        onShortDescriptionDirtyChange={handleShortDescriptionDirtyChange}
      />

      <div className="p-6" style={{ marginTop: "30px" }}>
        <StepDetailsTable
          stepDetails={stepDetails}
          onStepDetailsChange={handleStepDetailsChange}
          editingIndex={editingStepIndex}
          onEditingIndexChange={setEditingStepIndex}
          isHideStepDetailsHeading={isHideStepDetailsHeading}
          onHideStepDetailsHeadingChange={handleHideStepDetailsHeadingChange}
          onPartsModalOpen={() => { }}
          onAddRow={addStepRow}
          onUnsavedChanges={() => {
            setHasUnsavedChanges(true);
          }}
          onShowWarning={(message, type) => showSnackbar(message, type)}
          editingBomIndex={editingBomIndex}
        />

        <BOMTable
          bomTable={bomTable}
          onBomTableChange={handleBomTableChange}
          editingIndex={editingBomIndex}
          onEditingIndexChange={setEditingBomIndex}
          isHideBomHeading={isHideBomHeading}
          onHideBomHeadingChange={handleHideBomHeadingChange}
          units={units}
          onAddRow={addBomRow}
          onUnsavedChanges={() => {
            setHasUnsavedChanges(true);
          }}
          onValidationError={(message) => showSnackbar(message, "error")}
          onShowWarning={(message, type) => showSnackbar(message, type)}
          editingStepIndex={editingStepIndex}
          mode={mode}
        />
      </div>

      {/* Category Modals */}
      {categoryModalOpenForBom && (
        <CategoryPopup
          handleSelectCategory={handleBomTableCategoryChange}
          onClose={setCategoryModalOpenForBom}
          disableFilter={true}
          cat_type={"Step > BOM Table for Step Title"}
        />
      )}
      {categoryModalOpenForPart && (
        <CategoryPopup
          handleSelectCategory={handlePartNumberCategoryChange}
          onClose={setCategoryModalOpenForPart}
          disableFilter={true}
          cat_type={"Step > Item Code Column Title"}
        />
      )}

      {/* Save Modal */}
      <SaveModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleConfirmSave}
        title={`Save ${stepTitle}`}
        subtitle={`For ${partnumber} > Rev ${revision} > ${sectionTitle} > ${stepTitle}`}
        editorName={localStorage.getItem("user_name") || "Unknown User"}
        comment={comment}
        onCommentChange={(e) => setComment(e.target.value)}
        isLoading={isLoading}
        showLoader={true}
      />

      {/* Loading overlay */}
      {isLoading && <Loader text="Saving step..." />}

      {/* Snackbar */}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          procedurePartNumber={partnumber}
          procedureRevision={revision}
          versionHistories={mergedVersionHistories}
          mode={mode}
          breadcrumbText={versionHistoryBreadcrumbFallback}
          revisionTreeNodes={allNodesData}
          nodeId={
            currentStepData?.tree_node_id || step_id_ref || ""
          }
          nodeIdField="step_tree_node_id"
          nodeHistoryMatchIds={
            Array.isArray(stepVersionHistoryMatchIds) &&
            stepVersionHistoryMatchIds.length > 0
              ? stepVersionHistoryMatchIds
              : undefined
          }
          nodeHistoryStepOrdinal={
            globalStepOrdinalForNodeHistory != null &&
            Number.isFinite(Number(globalStepOrdinalForNodeHistory))
              ? Number(globalStepOrdinalForNodeHistory)
              : undefined
          }
          procedureRevisionIdForNodeHistory={revisionId}
          restrictNodeHistoryToProcedureRevision={
            allVersionHistories.length === 0
          }
          emptyMessage={
            mode === "add"
              ? "No version history available for new steps"
              : "No version history available"
          }
          onViewDetails={(row) => {
            const aid = row.audit_trail_id || row.audit_trail_id_ref;
            if (!aid) return;
            setShowVersionHistory(false);
            navigate(`/audit-trail/preview/${aid}`);
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
          exportCsvDisabled={versionHistoryTableLoading}
        />
      )}
    </div>
  );
};

export default AddEditStep;
